/**
 * Path-based router for SPA
 * Handles navigation, route parsing, and history management
 */

class Router {
    constructor() {
        this.listeners = [];
        this.basePath = '';
        this.detectBasePath();
        this.setupPopStateListener();
    }

    /**
     * Detect the base path from the current URL
     */
    detectBasePath() {
        const path = window.location.pathname;
        // Detect locale prefix for all supported locales
        const locales = ['ru', 'es', 'zh', 'id'];
        for (const locale of locales) {
            if (path.startsWith(`/${locale}/`) || path === `/${locale}`) {
                this.basePath = `/${locale}`;
                return;
            }
        }
    }

    /**
     * Navigate to a new route using pushState
     * @param {string} path - The path to navigate to (e.g., '/home/123')
     * @param {boolean} replace - Use replaceState instead of pushState
     */
    navigate(path, replace = false) {
        let fullPath = path;
        // Prepend base path if it's missing and path is absolute
        if (this.basePath && path.startsWith('/') && !path.startsWith(this.basePath)) {
            fullPath = this.basePath + (path === '/' ? '' : path);
        }

        if (replace) {
            window.history.replaceState(null, '', fullPath);
        } else {
            window.history.pushState(null, '', fullPath);
        }
        this.notifyListeners();
    }

    /**
     * Get the current pathname (stripped of base path)
     * @returns {string} Current pathname
     */
    getCurrentPath() {
        let path = window.location.pathname;
        if (this.basePath && (path === this.basePath || path.startsWith(this.basePath + '/'))) {
            path = path.substring(this.basePath.length) || '/';
        }
        return path;
    }

    /**
     * Parse the current route and extract parameters
     * @returns {Object} Route info with path and params
     */
    getCurrentRoute() {
        const path = this.getCurrentPath();
        return {
            path,
            params: this.parseParams(path)
        };
    }

    /**
     * Parse URL parameters from path
     * @param {string} path - The path to parse
     * @returns {Object} Extracted parameters
     */
    parseParams(path) {
        const segments = path.split('/').filter(s => s);
        const params = {};

        // Handle different route patterns
        if (segments[0] === 'claim' && segments[1]) {
            params.token = segments[1];
        } else if (segments[0] === 'home' && segments[1]) {
            params.groupId = segments[1];

            if (segments[2] === 'receipt' && segments[3]) {
                params.expenseId = segments[3];
                params.viewType = 'receipt';
            } else if (segments[2] === 'simple' && segments[3]) {
                params.expenseId = segments[3];
                params.viewType = 'simple';
            } else if (segments[2] === 'payback' && segments[3] && segments[4]) {
                params.creditor = parseInt(segments[3]);
                params.debtor = parseInt(segments[4]);
            }
        } else if (segments[0] === 'group' && segments[1]) {
            // Legacy route support
            params.groupId = segments[1];
        }

        return params;
    }

    /**
     * Register a callback for route changes
     * @param {Function} callback - Function to call on route change
     */
    onRouteChange(callback) {
        this.listeners.push(callback);
    }

    /**
     * Remove a route change listener
     * @param {Function} callback - The callback to remove
     */
    offRouteChange(callback) {
        this.listeners = this.listeners.filter(cb => cb !== callback);
    }

    /**
     * Notify all listeners of route change
     */
    notifyListeners() {
        const route = this.getCurrentRoute();
        this.listeners.forEach(callback => {
            try {
                callback(route);
            } catch (error) {
                console.error('Error in route change listener:', error);
            }
        });
    }

    /**
     * Setup popstate listener for browser back/forward
     */
    setupPopStateListener() {
        window.addEventListener('popstate', () => {
            this.notifyListeners();
        });
    }

    /**
     * Parse params for the current path (convenience wrapper)
     * @returns {Object} Extracted parameters for the current route
     */
    getParams() {
        return this.parseParams(this.getCurrentPath());
    }

    /**
     * Go back in history
     */
    back() {
        window.history.back();
    }

    /**
     * Go forward in history
     */
    forward() {
        window.history.forward();
    }
}

// Create singleton instance
const router = new Router();

// Export both the instance and the class
export { router, Router };
export default router;
